/**
 * SwatchSizeAvailability
 *
 * After the initial page render, fetches variant availability for every color
 * swatch in the product-grouping component and marks swatches that don't have
 * an available variant in the currently selected size with the class
 * `is-soldout-in-size`.
 *
 * Reacts to:
 *  - `document` "variant:change"      — user clicked a size / sub-option button
 *  - `window`   "productGroupingInjected" — user clicked a color swatch and the
 *                                           new product HTML was injected
 */

class SwatchSizeAvailability {
  constructor() {
    this._cache = new Map();
    this._timer = null;
    this._lastSizeValue = null;
    this._checkGen = 0;
    this._pendingColorChange = false;

    const init = () => {
      this._check();
      this._bindEvents();
    };

    if ('requestIdleCallback' in window) {
      requestIdleCallback(init, { timeout: 2000 });
    } else {
      setTimeout(init, 400);
    }
  }

  // ─── Events ───
  _bindEvents() {
    document.addEventListener('variant:change', () => this._scheduleCheck(false));

    window.addEventListener('productGroupingInjected', () => this._scheduleCheck(true));
  }

  _scheduleCheck(colorChanged = false, delay = 80) {
    this._pendingColorChange = this._pendingColorChange || colorChanged;
    clearTimeout(this._timer);
    this._timer = setTimeout(() => {
      const wasColorChange = this._pendingColorChange;
      this._pendingColorChange = false;
      this._check(wasColorChange);
    }, delay);
  }

  // ─── DOM helpers ───
  _getSwatchButtons() {
    return Array.from(
      document.querySelectorAll(
        'product-grouping [js-request-new-product][data-product-handle]'
      )
    );
  }

  _getCurrentSizeValue() {
    const sizeRow = document.querySelector(
      'product-grouping .product-grouping__options.is-size'
    );
    if (sizeRow) {
      const active = sizeRow.querySelector(
        'button[js-update-variant].is-active, button[js-update-variant][aria-checked="true"]'
      );
      if (active?.dataset.value) return active.dataset.value;
    }

    const anyActive = document.querySelector(
      'product-grouping button[js-update-variant].is-active'
    );
    return anyActive?.dataset.value ?? null;
  }

  // ─── Data fetching ───
  async _fetchVariants(handle) {
    if (this._cache.has(handle)) return this._cache.get(handle);

    try {
      const res = await fetch(`/products/${encodeURIComponent(handle)}.js`);
      if (!res.ok) return null;
      const { variants } = await res.json();
      this._cache.set(handle, variants ?? []);
      return this._cache.get(handle);
    } catch {
      return null;
    }
  }

  async _fetchVariantsAvailabilityBatch(variantIds = []) {
    const ids = [...new Set((variantIds || []).map(String).filter(Boolean))];
    if (!ids.length) return {};

    const accessToken = window.__VPV?.shop?.storefrontToken;
    const store = window.__VPV?.shop?.name;
    const locale = window.__VPV?.shop?.locale || 'en-US';

    if (!accessToken || !store) return {};

    const query = `
      query GetVariantsAvailability($ids: [ID!]!) {
        nodes(ids: $ids) {
          ... on ProductVariant {
            id
            availableForSale
          }
        }
      }
    `;

    try {
      const res = await fetch(
        `https://${store}.myshopify.com/api/2025-01/graphql.json`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': accessToken,
            Accept: 'application/json',
            'Accept-Language': locale,
          },
          body: JSON.stringify({
            query,
            variables: {
              ids: ids.map((id) => `gid://shopify/ProductVariant/${id}`),
            },
          }),
        }
      );

      if (!res.ok) return {};

      const result = await res.json();
      const availability = {};

      for (const node of result?.data?.nodes || []) {
        if (!node?.id) continue;
        availability[node.id.split('/').pop()] = node.availableForSale === true;
      }

      return availability;
    } catch {
      return {};
    }
  }

  _isVariantSellableFromMap(variant, availabilityMap) {
    if (!variant?.id) return false;
    const sellable = availabilityMap[String(variant.id)];
    return sellable === true;
  }

  // ─── Availability check ──
  async _check(colorChanged = false) {
    const swatches = this._getSwatchButtons();
    if (swatches.length === 0) return;

    const sizeValue = this._getCurrentSizeValue();

    if (!colorChanged && sizeValue === this._lastSizeValue) return;
    this._lastSizeValue = sizeValue;

    if (!sizeValue) {
      swatches.forEach(btn => btn.classList.remove('is-soldout-in-size'));
      return;
    }

    const nonActiveSwatches = swatches.filter(btn => !btn.classList.contains('is-active'));
    const handles = [
      ...new Set(nonActiveSwatches.map(btn => btn.dataset.productHandle).filter(Boolean)),
    ];

    const gen = ++this._checkGen;

    const results = await Promise.all(
      handles.map(async handle => ({
        handle,
        variants: await this._fetchVariants(handle),
      }))
    );

    if (gen !== this._checkGen) return;

    const variantIdsForSize = [];
    for (const { variants } of results) {
      if (!variants) continue;
      variants
        .filter((v) => v.option2 === sizeValue)
        .forEach((v) => variantIdsForSize.push(v.id));
    }

    const availabilityMap = await this._fetchVariantsAvailabilityBatch(variantIdsForSize);

    const available = {};
    for (const { handle, variants } of results) {
      if (!variants) continue;
      available[handle] = variants.some(
        (v) =>
          v.option2 === sizeValue &&
          this._isVariantSellableFromMap(v, availabilityMap)
      );
    }

    // Active swatch: clear any previous mark
    swatches
      .filter(btn => btn.classList.contains('is-active'))
      .forEach(btn => btn.classList.remove('is-soldout-in-size'));

    nonActiveSwatches.forEach(btn => {
      const handle = btn.dataset.productHandle;
      if (handle in available) {
        btn.classList.toggle('is-soldout-in-size', !available[handle]);
      }
    });
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new SwatchSizeAvailability());
} else {
  new SwatchSizeAvailability();
}
